import { useEffect, useState } from 'react'
import { Modal, Select, Space, Button, Tag, Table, Message, Empty } from '@arco-design/web-react'
import { get } from '@/api/client'
import { parseChannelModels } from '@/api/channels'
import type { Channel } from '@/types'

interface Props {
  visible: boolean
  channel: Channel | null
  onCancel: () => void
}

interface TestResult {
  model: string
  success: boolean
  time?: number
  message?: string
}

async function testChannelModel(channelId: number, model: string): Promise<TestResult> {
  try {
    const res = (await get<{ success?: boolean; time?: number; message?: string }>(
      `/api/channel/test/${channelId}`,
      { model }
    )) as { success?: boolean; time?: number; message?: string }
    return {
      model,
      success: res?.success !== false,
      time: res?.time,
      message: res?.message,
    }
  } catch (err: unknown) {
    return {
      model,
      success: false,
      message: err instanceof Error ? err.message : '请求失败',
    }
  }
}

export default function ModelTestModal({ visible, channel, onCancel }: Props) {
  const [selected, setSelected] = useState<string[]>([])
  const [results, setResults] = useState<TestResult[]>([])
  const [testing, setTesting] = useState(false)

  useEffect(() => {
    if (visible && channel) {
      setSelected(parseChannelModels(channel.models as unknown))
      setResults([])
    }
  }, [visible, channel])

  if (!channel) return null

  const modelOptions = parseChannelModels(channel.models as unknown)

  const runAll = async () => {
    if (!selected.length) {
      Message.warning('请至少选择一个模型')
      return
    }
    setTesting(true)
    setResults([])
    const queue = [...selected]
    const collected: TestResult[] = []
    while (queue.length) {
      const model = queue.shift()!
      const r = await testChannelModel(channel.id, model)
      collected.push(r)
      setResults([...collected])
    }
    setTesting(false)
  }

  const columns = [
    { title: '模型', dataIndex: 'model', width: 220 },
    {
      title: '结果',
      dataIndex: 'success',
      width: 100,
      render: (v: boolean) => (v ? <Tag color="green">成功</Tag> : <Tag color="red">失败</Tag>),
    },
    {
      title: '耗时 (s)',
      dataIndex: 'time',
      width: 120,
      render: (v?: number) => (typeof v === 'number' ? v.toFixed(2) : '-'),
    },
    {
      title: '信息',
      dataIndex: 'message',
      render: (v?: string) => v || '-',
    },
  ]

  return (
    <Modal
      title={`模型可用性测试 · ${channel.name}`}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      style={{ width: 720 }}
    >
      <Space direction="vertical" size="medium" style={{ width: '100%' }}>
        <Select
          mode="multiple"
          placeholder="选择要测试的模型"
          value={selected}
          onChange={setSelected}
          options={modelOptions.map((m) => ({ label: m, value: m }))}
          showSearch
          allowClear
          style={{ width: '100%' }}
        />
        <Space>
          <Button type="primary" loading={testing} onClick={runAll}>
            {testing ? '测试中...' : '开始测试'}
          </Button>
          <Button onClick={() => setResults([])} disabled={testing}>
            清空结果
          </Button>
        </Space>
        {results.length === 0 ? (
          <Empty description={testing ? '正在逐个测试...' : '暂无测试结果'} />
        ) : (
          <Table
            rowKey="model"
            columns={columns}
            data={results}
            pagination={false}
            size="small"
            scroll={{ y: 360 }}
          />
        )}
      </Space>
    </Modal>
  )
}
